import type { ReactNode } from 'react';
import { styled } from 'styled-components';
import type { PartialDeep } from 'type-fest';

import type { ApiAddress } from 'api/types/ApiAddress';
import type { ApiAreaOfFocus } from 'api/types/ApiAreaOfFocus';
import type { ApiFilestackImageData } from 'api/types/ApiFilestackImageData';
import type { ApiPreferredListingType } from 'api/types/ApiPreferredListingType';
import type { ApiSlideshowData } from 'api/types/ApiSlideshowData';
import type { ApiOrgId } from 'api/types/ApiTypedId';
import { Box } from 'components/Box/Box';
import { AddressFormSeparateFieldsSwitch } from 'components/Form/AddressForm/AddressFormSeparateFieldsSwitch';
import { CheckboxField } from 'components/Form/CheckboxField';
import { OrgSizeSelectField } from 'components/Form/Fields/OrgSize/OrgSizeSelectField';
import { IconCheckboxListingTypesField } from 'components/Form/Fields/PreferredListingTypes/IconCheckboxListingTypesField';
import { FormContextConsumer } from 'components/Form/FormContext';
import { ImagePickerField } from 'components/Form/ImagePickerField';
import { InputField } from 'components/Form/InputField';
import { LogoPickerField } from 'components/Form/LogoPickerField';
import { MultiImagePickerField } from 'components/Form/MultiImagePickerField';
import { MultiInputField } from 'components/Form/MultiInputField';
import { MultiSelectField } from 'components/Form/MultiSelectField';
import { PhoneField } from 'components/Form/PhoneField';
import { RadioButtonsField } from 'components/Form/Radio/RadioButtonsField';
import { SelectInputField } from 'components/Form/SelectInputField';
import { LegacyTooltip } from 'components/LegacyTooltip/LegacyTooltip';
import { StyledNativeLinkWithRef } from 'components/Link/StyledNativeLinkWithRef';
import { Text } from 'components/Text/Text';
import type { I18nFn } from 'i18n/types/I18nFn';
import type { I18nLocale } from 'i18n/types/I18nLocale';
import { ListingRteField } from 'modules/listing/fields/ListingRteField';
import type { ApiOrgSize } from 'modules/listing/orgs/api/types/ApiOrgSize';
import type { ApiOrgTimeActive } from 'modules/listing/orgs/api/types/ApiOrgTimeActive';
import { ApiOrgType } from 'modules/listing/orgs/api/types/ApiOrgType';
import { orgDashboardApiPartialUpdateOrg } from 'modules/orgDashboard/api/orgDashboardApiOrg';
import type {
  OrgDashboardApiOrg,
  OrgDashboardApiYesNoDontKnow,
} from 'modules/orgDashboard/api/types/OrgDashboardApiOrg';
import { colors, fontWeights, transition } from 'theme/theme';
import type { Nullable } from 'types/Nullable';
import { AREAS_OF_FOCUS_RADIO_OR_SELECT_OPTIONS } from 'utils/constants/general/areasOfFocus';
import { LOCALE_RADIO_OR_SELECT_OPTIONS } from 'utils/constants/general/locales';
import { ORG_TYPE_RADIO_OR_SELECT_OPTIONS } from 'utils/constants/general/orgTypes';

import { LabelWithTooltip } from './LabelWithTooltip';
import { NewOrgNameInputField } from './NewOrgNameInputField';
import { OrgFieldInfo } from './OrgFieldInfo';
import { OrganizationContactField } from './OrganizationContactField';
import { ORG_TYPE_DETAIL_COUNT } from './orgDetailCount';
import { ORG_RELATED_JOB_AREAS } from './orgRelatedJobAreas';

const ComplianceLink = styled.a`
  position: relative;
  display: inline-block;
  color: ${colors.brandBlue};
  font-weight: ${fontWeights.semiBold};
  transition: color ${transition};

  &:hover {
    color: ${colors.hoverBlue};
  }
`;

export type OrgFieldsValues = {
  locale: I18nLocale;
  simpleName: string;
  name: string;
  logo: ApiFilestackImageData;
  is501c3: boolean;
  orgType: ApiOrgType;
  orgTypeDetailsIntro: string;
  services: string;
  clients: string;
  isSocialEnterprise: boolean;
  isBCorp: OrgDashboardApiYesNoDontKnow;
  isHospiceProvider: OrgDashboardApiYesNoDontKnow;
  governmentId: string;
  linkedinId: number;
  linkedinName: string;
  vatNumber: string;
  description: string;
  mission: string;
  areasOfFocus: ApiAreaOfFocus[];
  links: string[];
  email: string;
  phone: string;
  banner: ApiFilestackImageData;
  slideshowItems: ApiSlideshowData[];
  locationType: string;
  address: ApiAddress;
  orgSize: ApiOrgSize;
  preferredListingTypes: ApiPreferredListingType[];
  relatedJobAreas: boolean;
  relatedJobCount: number;
  orgMeetsCriteria: boolean;
  howToGetInvolved: string;
  timeActive: ApiOrgTimeActive;
  inAccordance: boolean;
};

export type OrgFormValues = Nullable<PartialDeep<OrgFieldsValues>> & {
  preview?: boolean;
  validate: (keyof OrgFieldsValues)[];
};

function getDescriptionDescription(
  i18n: I18nFn,
  orgType: ApiOrgType | null | undefined,
): string {
  if (orgType === ApiOrgType.NONPROFIT) {
    return i18n(
      'Provide details that give our community insight into your organization, values, whom you serve, etc.',
    );
  }
  if (orgType === ApiOrgType.CORPORATION) {
    return i18n(
      'A multinational clothing retailer with a mission of sustainability to the environment and a goal of reducing poverty across the globe.',
    );
  }
  return i18n(
    'Provide details that give our community insight into your organization, like your mission, values, whom you serve, etc.',
  );
}

function getAddressLabelText(
  i18n: I18nFn,
  locationType: 'ONSITE' | 'REMOTE' | 'HYBRID',
) {
  if (locationType === 'REMOTE') {
    return i18n(
      'Although remote, what location is this organization primarily associated with?',
    );
  }
  if (locationType === 'HYBRID') {
    return i18n(
      'What location is this organization primarily associated with?',
    );
  }
  return null;
}

type Args = {
  i18n: I18nFn;
  orgName: string | null | undefined;
  orgId?: ApiOrgId;
  values: OrgFormValues;
  orgMatch: { id: string; name: string } | null;
  setOrgMatch: (arg0: { id: string; name: string } | null) => void;
  simple?: boolean;
  isStaff?: boolean;
  isEdit?: boolean;
  hasOrgLogo?: boolean;
  onUpdateOrg: (newOrg: OrgDashboardApiOrg) => void;
};

export function getOrgFields({
  i18n,
  orgName,
  orgId,
  values,
  orgMatch,
  setOrgMatch,
  simple,
  isStaff,
  isEdit,
  hasOrgLogo,
  onUpdateOrg,
}: Args): Record<string, ReactNode> {
  return {
    locale: (
      <RadioButtonsField
        name="locale"
        label={
          /* TRANSLATORS: org form locale field  */
          i18n('Language')
        }
        buttons={LOCALE_RADIO_OR_SELECT_OPTIONS()}
      />
    ),
    simpleName: (
      <NewOrgNameInputField
        name="name"
        autoComplete="off"
        label={
          /* TRANSLATORS: Form field for org name */
          `${i18n('Organization Name')}*`
        }
        description={i18n(
          'Enter your organization’s name to see if it already has an account on Idealist.',
        )}
        orgMatch={orgMatch}
        setOrgMatch={setOrgMatch}
      />
    ),
    name: (
      <InputField
        name="name"
        autoComplete="off"
        label={
          /* TRANSLATORS: Form field for org name */
          `${i18n('Organization Name')}*`
        }
        description={i18n(
          'Enter your organization’s name to see if it already has an account on Idealist.',
        )}
      />
    ),
    logo: (
      <FormContextConsumer>
        {({ updateValue }) => (
          <LogoPickerField
            name="logo"
            label={i18n('Logo')}
            description={
              hasOrgLogo
                ? i18n(
                    'Recommended minimum dimensions: 360 x 360. For the best appearance, please upload a square version of your logo. Horizontal logos may not display optimally.',
                  )
                : i18n(
                    'Profiles with a logo are more likely to be noticed. Add your organization’s logo below. Recommended minimum dimensions: 360 x 360. For the best appearance, please upload a square version of your logo. Horizontal logos may not display optimally.',
                  )
            }
            recommendedDimensions={{
              width: 360,
              height: 360,
            }}
            altText={orgName ? `${orgName}` : i18n('Organization')}
            onChange={async (image) => {
              // Update the form's state
              updateValue('logo', image);

              // Prevent real-time persistence if the field is used by a staff user, which don't have the authorization
              // to use the API endpoint below.
              if (!isStaff && orgId) {
                // Eagerly persist the change
                return orgDashboardApiPartialUpdateOrg({
                  id: orgId,
                  logo: image,
                }).then((response) => {
                  onUpdateOrg(response);
                });
              }
            }}
            extraAnalyticsData={{
              context: 'Edit Org Page',
              file_type: 'Org Logo',
              file_label: 'Logo',
            }}
          />
        )}
      </FormContextConsumer>
    ),
    is501c3: (
      <>
        <RadioButtonsField
          name="is501c3"
          label={`${
            isStaff
              ? i18n('Is the organization a registered tax-exempt 501(c)(3)?')
              : i18n('Is your organization a registered tax-exempt 501(c)(3)')
          }*`}
          buttons={[
            {
              label: i18n('Yes'),
              value: true,
            },
            {
              label: i18n('No'),
              value: false,
            },
          ]}
        />
        {simple &&
          values.address?.country === 'US' &&
          values.is501c3 === true && (
            <OrgFieldInfo>
              {i18n(
                'Great! As a 501(c)(3), you can create new opportunity ' +
                  'listings once you complete this organization profile. ' +
                  'They will be live once your organization is approved.',
              )}
            </OrgFieldInfo>
          )}
      </>
    ),
    orgType: (
      <RadioButtonsField
        name="orgType"
        label={
          /* TRANSLATORS: Form field for org type (nonprofit, gov, etc.) */
          `${i18n('Organization Type')}*`
        }
        description={i18n(
          'Review our {{link}}guidelines{{/link}} for any organization or entity wishing to post on idealist.org',
          {
            link: (
              // oxlint-disable-next-line jsx-a11y/anchor-has-content
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.idealist.org/en/help/what-organizations-can-post-on-idealist"
              />
            ),
          },
        )}
        buttons={ORG_TYPE_RADIO_OR_SELECT_OPTIONS(i18n).map(
          ({ value, label }) => {
            let tooltipText;

            if (value === 'CORPORATION') {
              tooltipText = i18n(
                /* TRANSLATORS: Tooltip next to 'Business' label in organization type when creating new non-501c organization */

                `Social enterprises and approved for-profit companies are allowed to post jobs on Idealist that adhere to our guidelines.`,
              );
            }

            if (value === 'COMMUNITY') {
              tooltipText = i18n(
                /* TRANSLATORS: Tooltip next to 'Community Group' label in organization type when creating new non-501c organization */

                'Common interest and other mission-driven, noncommercial groups may now post listings that adhere to the Idealist guidelines.',
              );
            }

            return tooltipText
              ? {
                  label: (
                    <LabelWithTooltip
                      qaIdPrefix={`org-type-${value}`}
                      name={label}
                      tooltipText={tooltipText}
                    />
                  ),
                  value,
                }
              : {
                  label,
                  value,
                };
          },
        )}
        columns={2}
      />
    ),
    orgTypeDetailsIntro: ORG_TYPE_DETAIL_COUNT[values.orgType!] ? (
      <Box mb={3}>
        <OrgFieldInfo>
          {ORG_TYPE_DETAIL_COUNT[values.orgType!] > 1 &&
          ORG_TYPE_DETAIL_COUNT[values.orgType!] <= 3
            ? i18n(
                'Your responses to the following %(questionCount)s questions will not be publicly visible on your profile.',
                { questionCount: ORG_TYPE_DETAIL_COUNT[values.orgType!] },
              )
            : i18n(
                'Your responses to the following questions will not be publicly visible on your profile.',
              )}
        </OrgFieldInfo>
      </Box>
    ) : (
      // TODO: Fix this the next time the file is edited.
      // oxlint-disable-next-line react/jsx-no-useless-fragment
      <></>
    ),
    services: (
      <InputField
        label={i18n(
          'What services do you provide to nonprofit organizations and/or government agencies?',
        )}
        name="services"
      />
    ),
    clients: (
      <InputField
        label={i18n(
          'Please include names of up to three nonprofit organizations and/or government agencies that are currently your clients.',
        )}
        name="clients"
      />
    ),
    isSocialEnterprise: (
      <RadioButtonsField
        name="isSocialEnterprise"
        label={
          <Box display="flex" alignItems="center">
            {i18n('Is your company a Social Enterprise?')}*
            <Box position="relative" zIndex={1} ml="4px">
              <LegacyTooltip>
                {
                  /* TRANSLATORS: Tooltip next to question asking if org being created is a social enterprise or not */
                  i18n(
                    `Social Enterprises are social-impact entities whose primary purpose is to use the company's for-profit business model to achieve a social mission.`,
                  )
                }
              </LegacyTooltip>
            </Box>
          </Box>
        }
        buttons={[
          {
            label: i18n('Yes'),
            value: 'YES',
          },
          {
            label: i18n('No'),
            value: 'NO',
          },
          {
            label: i18n("I don't know"),
            value: 'DONT_KNOW',
          },
        ]}
      />
    ),
    isBCorp: (
      <RadioButtonsField
        name="isBCorp"
        label={
          <>
            {i18n('Is your company a B Corporation')}
            <Box
              display="inline-block"
              position="relative"
              zIndex={1}
              ml="2px"
              mr="4px"
            >
              <LegacyTooltip>
                {
                  /* TRANSLATORS: Tooltip explaining to org creator what is a B Corporation */
                  i18n(
                    'Find more information at {{link}}BCorporation.net{{/link}}',
                    {
                      link: (
                        // oxlint-disable-next-line jsx-a11y/anchor-has-content
                        <a
                          target="_blank"
                          rel="noopener noreferrer"
                          href="https://bcorporation.net/"
                        />
                      ),
                    },
                  )
                }
              </LegacyTooltip>
            </Box>
            {i18n(
              'or legally registered as a Benefit Corporation or equivalent designation',
            )}
            <Box
              display="inline-block"
              position="relative"
              zIndex={1}
              ml="2px"
              mr="4px"
            >
              <LegacyTooltip>
                {
                  /* TRANSLATORS: Tooltip explaining to org creator what is a Benefit Corporation */
                  i18n(
                    "Find more information via {{link}}Wikipedia's entry on benefit corporations{{/link}}",
                    {
                      link: (
                        // oxlint-disable-next-line jsx-a11y/anchor-has-content
                        <a
                          target="_blank"
                          rel="noopener noreferrer"
                          href="https://en.wikipedia.org/wiki/Benefit_corporation"
                        />
                      ),
                    },
                  )
                }
              </LegacyTooltip>
            </Box>
            ?*
          </>
        }
        buttons={[
          {
            label: i18n('Yes'),
            value: 'YES',
          },
          {
            label: i18n('No'),
            value: 'NO',
          },
          {
            label: i18n("I don't know"),
            value: 'DONT_KNOW',
          },
        ]}
      />
    ),
    isHospiceProvider: (
      <RadioButtonsField
        name="isHospiceProvider"
        label={<>{i18n('Is your company a hospice provider?')}*</>}
        buttons={[
          {
            label: i18n('Yes'),
            value: 'YES',
          },
          {
            label: i18n('No'),
            value: 'NO',
          },
          {
            label: i18n("I don't know"),
            value: 'DONT_KNOW',
          },
        ]}
      />
    ),
    governmentId: (
      <InputField
        name="governmentId"
        label={i18n('Government ID')}
        description={i18n(
          'Not visible to public. Add your EIN, charity number, or other tax ID that can be used to verify that your organization is qualified to post opportunities on Idealist.',
        )}
      />
    ),
    linkedinId: (
      <InputField
        name="linkedinId"
        label={i18n('LinkedIn ID')}
        description={i18n(
          'If your organization is on LinkedIn, this helps ensure your job is posted in association with the correct LinkedIn profile. Learn more about where to find your Company ID {{link}}here{{/link}}.',
          {
            link: (
              // oxlint-disable-next-line jsx-a11y/anchor-has-content
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.linkedin.com/help/linkedin/answer/a415420/associate-your-linkedin-company-id-with-the-linkedin-job-board-faqs?lang=en"
              />
            ),
          },
        )}
      />
    ),
    linkedinName: (
      <InputField name="linkedinName" label={i18n('LinkedIn Org Name')} />
    ),
    vatNumber: <InputField name="vatNumber" label={i18n('VAT Number')} />,
    description: (
      <FormContextConsumer<{ orgType: ApiOrgType }>>
        {({ values }) => (
          <ListingRteField
            name="description"
            label={
              values.orgType === ApiOrgType.NONPROFIT
                ? /* TRANSLATORS: Form field for org description */
                  i18n('More About Us')
                : /* TRANSLATORS: Form field for org description */
                  `${i18n('Description')}*`
            }
            description={getDescriptionDescription(i18n, values.orgType)}
            placeholder={
              values.orgType === ApiOrgType.CORPORATION
                ? i18n(
                    'A multinational clothing retailer with a mission of sustainability to the environment and a goal of reducing poverty across the globe.',
                  )
                : undefined
            }
          />
        )}
      </FormContextConsumer>
    ),
    mission: (
      <ListingRteField
        name="mission"
        label={i18n('Mission')}
        description={i18n('Share your mission with our community.')}
      />
    ),
    areasOfFocus: (
      <MultiSelectField
        name="areasOfFocus"
        label={i18n('Cause Areas')}
        description={i18n('Select up to 5 focus areas.')}
        options={AREAS_OF_FOCUS_RADIO_OR_SELECT_OPTIONS(i18n)}
      />
    ),
    donationLink: (
      <InputField
        name="donationLink"
        type="url"
        label={i18n('Donation Link')}
        description={i18n(
          'A button will appear on your profile so visitors can support your organization directly.',
        )}
        placeholder={i18n('https://example.com')}
      />
    ),
    links: (
      <FormContextConsumer<{ orgType: ApiOrgType }>>
        {({ values }) => (
          <MultiInputField
            name="links"
            label={
              !isEdit && values.orgType === ApiOrgType.COMMUNITY
                ? `${i18n('Links')}*`
                : i18n('Links')
            }
            description={
              values.orgType === ApiOrgType.CORPORATION
                ? i18n(
                    'Links to your website and social media pages that reflect your social good initiatives.',
                  )
                : i18n('Links to your website and social media pages.')
            }
            maxInputs={5}
            placeholder="https://example.org"
          />
        )}
      </FormContextConsumer>
    ),
    orgContact: (
      <OrganizationContactField
        isEdit={Boolean(isEdit)}
        isCommunity={values.orgType === ApiOrgType.COMMUNITY}
      />
    ),
    email: (
      <InputField
        name="email"
        type="email"
        label={i18n('Email')}
        description={i18n('Visible to the public')}
        placeholder={i18n('casey.smith@example.com')}
      />
    ),
    phone: (
      <PhoneField
        name="phone"
        label={i18n('Phone Number')}
        description={i18n('Visible to the public')}
        placeholder={i18n('e.g. 123 555-1234')}
        initialValueFormat="national"
      />
    ),
    banner: (
      <ImagePickerField
        name="banner"
        description={i18n('Recommended dimensions: 2880 x 400')}
        recommendedDimensions={{
          width: 2880,
          height: 400,
        }}
        label={i18n('Banner Image')}
        aspectRatio={7.2}
        format="banner"
        extraAnalyticsData={{
          context: 'Edit Org Page',
          file_type: 'Org Banner',
          file_label: 'Banner Image',
        }}
      />
    ),
    slideshowItems: (
      <MultiImagePickerField
        name="slideshowItems"
        description={i18n('Recommended dimensions: 200 x 200')}
        label={i18n('Image Gallery')}
        allowVideo
        width={200}
        height={200}
        extraAnalyticsData={{
          context: 'Edit Org Page Image Gallery',
          file_type: 'Org Image',
          file_label: 'Image',
        }}
      />
    ),
    locationType: (
      <RadioButtonsField
        large
        columns={[1, 1, 1]}
        name="locationType"
        buttons={[
          {
            label: i18n('Onsite'),
            value: 'ONSITE',
          },
          {
            label: i18n('Remote'),
            value: 'REMOTE',
          },
          {
            label: i18n('Hybrid'),
            value: 'HYBRID',
          },
        ]}
      />
    ),
    address: (
      <FormContextConsumer<{ locationType: 'ONSITE' | 'REMOTE' | 'HYBRID' }>>
        {({ values }) => (
          <AddressFormSeparateFieldsSwitch
            hideCountry={simple}
            canChangeLocationHidden
            showLocationButtonText={i18n('Display full street address')}
            hideLocationButtonText={i18n('Display only the city')}
            label={getAddressLabelText(i18n, values.locationType)}
          />
        )}
      </FormContextConsumer>
    ),
    orgSize: (
      <OrgSizeSelectField
        name="orgSize"
        label={
          [
            ApiOrgType.NONPROFIT,
            ApiOrgType.GOVERNMENT,
            ApiOrgType.COMMUNITY,
          ].includes(values.orgType!)
            ? i18n('How large is your organization?')
            : i18n('How large is your company?')
        }
        members={values.orgType === ApiOrgType.COMMUNITY}
      />
    ),
    preferredListingTypes: (
      <FormContextConsumer<{ orgType: ApiOrgType }>>
        {({ values }) => (
          <IconCheckboxListingTypesField
            name="preferredListingTypes"
            label={i18n('What is your organization most likely to post?')}
            newOrgType={values.orgType}
          />
        )}
      </FormContextConsumer>
    ),
    relatedJobAreas: (
      <RadioButtonsField
        name="relatedJobAreas"
        label={`${
          /* TRANSLATORS Are you looking to post listings within these areas? */
          i18n('Are you looking to post listings within these areas?')
        }*`}
        description={
          <ul style={{ margin: 0 }}>
            {ORG_RELATED_JOB_AREAS(i18n).map((area) => (
              <li key={area}>{area}</li>
            ))}
          </ul>
        }
        buttons={[
          {
            label: i18n('Yes'),
            value: true,
          },
          {
            label: i18n('No'),
            value: false,
          },
        ]}
      />
    ),
    relatedJobCount:
      values.relatedJobAreas !== false ? (
        <InputField
          name="relatedJobCount"
          label={`${
            /* TRANSLATORS About how many currently-filled positions at your company relate to one of the areas listed above? */
            i18n(
              'About how many currently-filled positions at your company relate to one of the areas listed above?',
            )
          }*`}
          type="number"
          placeholder="0"
          min={0}
          noWrap
        />
      ) : (
        <OrgFieldInfo>
          {
            /* TRANSLATORS Have something else in mind? Please apply and we’ll get back to you with feedback or follow-up questions. */
            i18n(
              'Have something else in mind? Please apply and we’ll get back to you with feedback or follow-up questions.',
            )
          }
        </OrgFieldInfo>
      ),
    // @NOTE: Not really a field...
    orgMeetsCriteria:
      values.orgType !== ApiOrgType.CORPORATION ||
      (values.orgType === ApiOrgType.CORPORATION &&
        values.relatedJobAreas &&
        values.relatedJobCount) ? (
        <OrgFieldInfo>
          {i18n(
            'Thank you! Once you submit your organization’s profile, our team will review to confirm your organization {{linkQualifies}}qualifies{{/linkQualifies}} to join the Idealist community. You can expect to hear back from us within two business days about the status of your organization profile. {{linkQualifies}}Get details about the requirements here{{/linkQualifies}}. If you don’t receive an email within that timeframe, please check your Junk/Spam folder, then {{linkContact}}contact us{{/linkContact}}.',
            {
              linkQualifies: (
                <StyledNativeLinkWithRef
                  target="_blank"
                  href={i18n(
                    'https://www.idealist.org/en/help/what-organizations-can-post-on-idealist',
                  )}
                />
              ),
              linkContact: (
                <StyledNativeLinkWithRef
                  target="_blank"
                  href={i18n('https://www.idealist.org/en/about/contact')}
                />
              ),
            },
          )}
        </OrgFieldInfo>
      ) : (
        // TODO: Fix this the next time the file is edited.
        // oxlint-disable-next-line react/jsx-no-useless-fragment
        <></>
      ),
    howToGetInvolved: (
      <InputField
        name="howToGetInvolved"
        label={`${
          /* TRANSLATORS Text asking org admins to describe how people can get involved with their group */
          i18n('How can people get involved with your group?')
        }*`}
        noWrap
      />
    ),
    timeActive: (
      <SelectInputField
        name="timeActive"
        label={`${
          /* TRANSLATORS Text asking org admins to describe how long their group has actively been operating */
          i18n('How long has your group been active for?')
        }*`}
        options={[
          {
            value: '',
            label: i18n('Select One'),
            disabled: true,
          },
          {
            value: '0_1_YEARS',
            label: i18n('0-1 years'),
          },
          {
            value: '1_2_YEARS',
            label: i18n('1-2 years'),
          },
          {
            value: '2_5_YEARS',
            label: i18n('2-5 years'),
          },
          {
            value: '5_10_YEARS',
            label: i18n('5-10 years'),
          },
          {
            value: '10_PLUS_YEARS',
            label: i18n('10+ years'),
          },
        ]}
      />
    ),
    inAccordance: (
      <FormContextConsumer<{ orgType: ApiOrgType }>>
        {({ values }) => {
          const components = {
            termsLink: (
              <ComplianceLink href="/en/terms-of-service" target="_blank" />
            ),
            privacyLink: (
              <ComplianceLink href="/en/privacy-policy" target="_blank" />
            ),
            guidelinesLink: (
              <ComplianceLink
                href="/en/help/what-are-community-guidelines"
                target="_blank"
              />
            ),
          };

          return (
            <div>
              <CheckboxField
                name="inAccordance"
                label={
                  <Text pt="5px" pr={[0, 24]} lineHeight={1.375}>
                    {values.orgType === ApiOrgType.COMMUNITY
                      ? /* TRANSLATORS: age and terms verification when creating an org */
                        i18n(
                          'I agree to Idealist’s {{termsLink}}Terms of Service{{/termsLink}}, {{privacyLink}}Privacy Policy{{/privacyLink}}, and {{guidelinesLink}}Community Guidelines{{/guidelinesLink}}, and confirm that I am at least 16 years old.',
                          components,
                        )
                      : i18n(
                          'I agree to Idealist’s {{termsLink}}Terms of Service{{/termsLink}}, {{privacyLink}}Privacy Policy{{/privacyLink}}, and {{guidelinesLink}}Community Guidelines{{/guidelinesLink}}, and confirm that I am at least 18 years old and authorized to post on behalf of this organization.',
                          components,
                        )}
                  </Text>
                }
              />
            </div>
          );
        }}
      </FormContextConsumer>
    ),
  };
}
